import { useEffect, useRef, useState } from 'react'

export interface ColumnVisibilityOption {
  id: string
  label: string
  /** Always visible, no toggle rendered — e.g. a primary identifier or an actions column. */
  locked?: boolean
}

interface ColumnVisibilityDropdownProps {
  columns: ColumnVisibilityOption[]
  isVisible: (id: string) => boolean
  onToggle: (id: string) => void
  onReset?: () => void
}

/**
 * PE-2254 — "Columns" toggle button + checklist, shared by every data table that wants
 * client-side column visibility. Purely presentational: visibility state and persistence
 * live in `useColumnVisibility`, passed in as props.
 */
export function ColumnVisibilityDropdown({ columns, isVisible, onToggle, onReset }: ColumnVisibilityDropdownProps) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 })

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const toggleOpen = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setMenuPos({ top: rect.bottom + 4, left: rect.right - 224 })
    }
    setOpen((o) => !o)
  }

  const toggleable = columns.filter((c) => !c.locked)

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleOpen}
        className="flex items-center gap-1.5 px-3 py-2 border border-border rounded-md text-sm font-medium text-foreground hover:bg-muted transition-colors"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="7" height="16" rx="1" />
          <rect x="14" y="4" width="7" height="16" rx="1" />
        </svg>
        Columns
      </button>

      {open && (
        <div
          ref={menuRef}
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 9999 }}
          className="w-56 bg-card border border-border rounded-[10px] shadow-lg overflow-hidden py-1"
        >
          {toggleable.length === 0 ? (
            <p className="px-4 py-2 text-sm text-muted-foreground">No optional columns.</p>
          ) : (
            toggleable.map((col) => (
              <label
                key={col.id}
                className="flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={isVisible(col.id)}
                  onChange={() => onToggle(col.id)}
                  className="h-4 w-4 rounded border-border"
                />
                {col.label}
              </label>
            ))
          )}
          {onReset && (
            <>
              <div className="border-t border-border mt-1" />
              <button
                type="button"
                onClick={onReset}
                className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted"
              >
                Reset to default
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}
